import type { ComponentsUnionType } from '@unifyapps/defs/types/block';
import { MODULE_AND_TEMPLATE_BLOCK_COMPONENT_TYPES } from '@unifyapps/defs/types/block';
import { useMemo } from 'react';
import { BlockDataAttributes } from '../const/blockDataAttributes';
import { useRuntimeBlockKey } from '../context/BlockRuntimeStateProvider';
import { useInterfaceStore } from '../stores/InterfaceStore/context';

// Defined in a leaf consts module (not here) so the element-picker bridge —
// bundled standalone for tensor code apps — can import the enum without pulling React in.
// Re-exported below so the ~40 existing importers of this hook file keep working unchanged.
export { BlockDataAttributes };

type Props = {
  id: string;
  component: ComponentsUnionType & {
    appearance?: { theme?: string };
  };
  pageId: string;
  blockStateId: string | undefined;
  blockVersion?: number;
  detachFromLayout?: boolean;
};

export const useBlockDataAttributes = (props: Props): Record<string, string> => {
  const {
    id,
    component: { componentType, appearance },
    pageId,
    blockStateId,
    blockVersion,
    detachFromLayout,
  } = props;
  const runTimeBlockKey = useRuntimeBlockKey();
  const pageScopeKey = useInterfaceStore().hooks.usePageScopeKey();

  return useMemo(() => {
    const dataAttributes: Record<string, string> = {
      [BlockDataAttributes.BlockId]: id,
      [BlockDataAttributes.BlockType]: componentType,
      ...(blockVersion ? { [BlockDataAttributes.BlockVersion]: blockVersion.toString() } : {}),
    };

    if (MODULE_AND_TEMPLATE_BLOCK_COMPONENT_TYPES.has(componentType)) {
      dataAttributes[BlockDataAttributes.InterfacePageId] = pageId;
    }

    if (detachFromLayout && pageScopeKey) {
      dataAttributes[BlockDataAttributes.PageScopeKey] = pageScopeKey;
    }

    if (runTimeBlockKey && blockStateId) {
      dataAttributes[BlockDataAttributes.BlockRunTimeBlockId] = blockStateId;
    }
    if (appearance?.theme) {
      dataAttributes[BlockDataAttributes.DataColorSchema] = appearance.theme;
    }

    return dataAttributes;
  }, [
    id,
    componentType,
    runTimeBlockKey,
    appearance?.theme,
    pageId,
    blockStateId,
    blockVersion,
    detachFromLayout,
    pageScopeKey,
  ]);
};
